import { useMemo } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import dayjs from "dayjs"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

const PREFIX = "company_shopify_variant_daily_sales"

/**
 * Shopify unit sales and revenue per product, fetched SEPARATELY from the
 * mega_feed view and merged client-side by Shopify product id.
 *
 * This is the revenue figure to trust. The view's `total_revenue` is GA4's own
 * purchase revenue, which is not populated for every tenant (Tracksmith reports
 * 0 against 106 orders); this cube is Shopify's own order data.
 *
 * Why not in the view: company_shopify_variant_daily_sales also names its date
 * column `md_date`, which collides with the GA4 cube's member of the same name.
 * Exposing only one of them would leave the other's measures unbounded — the
 * Shopify revenue column would silently report ALL-TIME totals next to 30-day
 * GA4 columns. A separate query gets its own correct date filter.
 *
 * Grain note: the source table is (company, variant, day), so revenue here is
 * summed across every variant of the product over the selected range.
 */

export const SHOPIFY_METRICS_MAP = {
  shopify_revenue: "Shopify Revenue",
  shopify_units_sold: "Units Sold",
}

export type TShopifyMetricKey = keyof typeof SHOPIFY_METRICS_MAP

export const SHOPIFY_METRIC_KEYS = Object.keys(
  SHOPIFY_METRICS_MAP,
) as TShopifyMetricKey[]

export const MONETARY_SHOPIFY_METRICS: TShopifyMetricKey[] = ["shopify_revenue"]

export type TShopifySalesByProductId = Record<string, Record<string, unknown>>

export default function useShopifySales({
  productIds,
  dateRange,
  enabled = true,
}: {
  productIds: string[]
  dateRange: [Date, Date]
  enabled?: boolean
}) {
  // Only the products on the current page, so this stays a small lookup.
  const productIdKey = productIds.join("|")

  const query = useMemo<Query>(() => {
    const filters: Filter[] = []

    if (productIds.length > 0) {
      filters.push({
        member: `${PREFIX}.product_id`,
        operator: "equals" as BinaryOperator,
        values: productIds,
      })
    }

    return {
      dimensions: [`${PREFIX}.product_id`],
      measures: [`${PREFIX}.revenue`, `${PREFIX}.units_sold`],
      timeDimensions: [
        {
          dimension: `${PREFIX}.md_date`,
          dateRange: [
            dayjs(dateRange[0]).startOf("day").format("YYYY-MM-DD"),
            dayjs(dateRange[1]).endOf("day").format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      filters,
    }
    // productIdKey stands in for the array so an equivalent list doesn't
    // rebuild the query object and refire the request.
  }, [productIdKey, dateRange])

  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: !enabled || productIds.length === 0,
  })

  const byProductId = useMemo<TShopifySalesByProductId>(() => {
    if (!resultSet) return {}

    return resultSet.rawData().reduce((acc: TShopifySalesByProductId, row) => {
      // product_id is numeric in ClickHouse; the feed's item_group_id is a
      // string, so key the lookup on the stringified form.
      const productId = String(row[`${PREFIX}.product_id`] ?? "")
      if (!productId) return acc

      acc[productId] = {
        shopify_revenue: row[`${PREFIX}.revenue`],
        shopify_units_sold: row[`${PREFIX}.units_sold`],
      }
      return acc
    }, {})
  }, [resultSet])

  return { shopifySalesByProductId: byProductId, isLoading, error }
}
